/**
 * Shaping for the Analytics "Team" tab and the "Quoted value" panel on
 * Business overview (get_owner_team in
 * supabase/migrations/20261027140000_dashboard_reports.sql).
 *
 * As with ownerData.ts, the SQL defines every number and this module only
 * arranges them; each figure carries the DrillSpec that lists its records.
 */
import { formatClock, formatPct, formatSgdCompact, monthEnd, monthLabel, share, type DrillSpec, type ExecTile, type Speed } from './ownerData';

export interface TeamPerson {
  /** owner_salesperson(quote_sent_by): 'unknown' = blank, 'system' = sent with no user recorded. */
  key: string;
  name: string;
  quotes_sent: number;
  lines_priced: number;
  quoted_value: number;
  lines_with_suggestion: number;
  lines_at_system_price: number;
  speed: Speed;
}

export interface TeamMonth {
  month: string;
  system_quotes: number;
  quoted_value: number;
  lines_priced: number;
}

export interface OwnerTeam {
  generated_at: string;
  range: { from: string; to: string };
  mailbox_scanned_through: string | null;
  monthly: TeamMonth[];
  people: TeamPerson[];
  unattributed: {
    manual_replies: number;
    system_replies: number;
    threads: number;
    manual_speed: Speed;
  };
  totals: {
    quotes_sent: number;
    lines_priced: number;
    quoted_value: number;
    lines_with_suggestion: number;
    lines_at_system_price: number;
    speed: Speed;
  };
}

/** Below this many office-hours quotes a person's median is shown, but marked as a small sample. */
export const FEW_QUOTES = 10;

const NOT_RECORDED: Record<string, string> = {
  unknown: 'sender left blank',
  system: 'sent before the sender was saved',
};

export interface TeamRow {
  key: string;
  name: string;
  /** Why the name is missing, for the 'Not recorded' rows. */
  caption: string | null;
  notRecorded: boolean;
  quotes: number;
  /** Share of all system quotes in the period, percent. */
  quoteShare: number | null;
  /** That share as a short label ('64%', '<1%'). */
  quoteShareText: string;
  linesPriced: number;
  quotedValue: number;
  speed: Speed;
  fewQuotes: boolean;
  atSystemPct: number | null;
  atSystem: number;
  withSuggestion: number;
  /** The person's quotes - what the row opens. */
  drill: DrillSpec;
  /** The office-hours quotes behind the median time to quote; null when there are none. */
  speedDrill: DrillSpec | null;
  /** The lines with a system suggestion behind "at the system's price"; null when there are none. */
  atSystemDrill: DrillSpec | null;
}

const nf = (n: number) => n.toLocaleString('en-SG');

/**
 * A share for a small label, from the counts: '64%'; '<1%' rather than a
 * misleading '0%' for a handful, and '>99%' rather than '100%' when some are
 * left out.
 */
export function shareText(part: number, whole: number): string {
  if (!whole) return '-';
  const pct = (100 * part) / whole;
  if (part > 0 && pct < 1) return '<1%';
  if (part < whole && pct > 99) return '>99%';
  return `${Math.round(pct)}%`;
}

const isNotRecorded = (key: string) => key === 'unknown' || key === 'system';

/** The records behind one salesperson: the quotes they sent in the period. */
function personDrill(person: Pick<TeamPerson, 'key' | 'name'>, from: string, to: string): DrillSpec {
  const name = isNotRecorded(person.key) ? 'Sender not recorded' : person.name;
  return {
    title: `${name}: quotes sent through the system`,
    kind: 'system_quotes',
    from,
    to,
    filter: { sent_by: person.key },
    note: 'Every quote this person sent through the system in the period. Wait (clock) is the time from the RFQ reaching the system to the quote going out; the median counts only RFQs that arrived Mon-Fri 09:00-18:00.',
  };
}

/** The office-hours quotes behind a person's median time to quote. */
function personSpeedDrill(person: TeamPerson, from: string, to: string): DrillSpec | null {
  if (person.speed.answered === 0) return null;
  const name = isNotRecorded(person.key) ? 'Sender not recorded' : person.name;
  return {
    title: `${name}: quotes for office-hours RFQs`,
    kind: 'system_quotes',
    from,
    to,
    filter: { sent_by: person.key, office: true },
    note: 'The quotes behind the median time to quote: RFQs that reached the system Mon-Fri 09:00-18:00. Wait (clock) runs from the RFQ reaching the system to the quote going out.',
    unlessIgnored: { ignoredTotal: person.quotes_sent, expected: person.speed.answered, fallback: personDrill(person, from, to) },
  };
}

/** The lines behind a person's "at the system's price" share. */
function personPricingDrill(person: TeamPerson, team: OwnerTeam): DrillSpec | null {
  if (person.lines_with_suggestion === 0) return null;
  const { from, to } = team.range;
  const name = isNotRecorded(person.key) ? 'Sender not recorded' : person.name;
  return {
    title: `${name}: lines sent with a system suggestion`,
    kind: 'pricing_lines',
    from,
    to,
    filter: { sent_by: person.key },
    note: 'Each line this person sent that had a system suggestion. Within tolerance = the price sent is within max(2%, a minimum by price band) of the suggestion.',
    unlessIgnored: { ignoredTotal: team.totals.lines_with_suggestion, expected: person.lines_with_suggestion, fallback: personDrill(person, from, to) },
  };
}

/**
 * One row per salesperson, most quotes first, with the rows whose sender was
 * not recorded last - they are a data gap, not a person.
 */
export function teamRows(team: OwnerTeam): TeamRow[] {
  const { from, to } = team.range;
  return team.people
    .map((p) => {
      const notRecorded = isNotRecorded(p.key);
      return {
        key: p.key,
        name: notRecorded ? 'Not recorded' : p.name,
        caption: notRecorded ? NOT_RECORDED[p.key] : null,
        notRecorded,
        quotes: p.quotes_sent,
        quoteShare: share(p.quotes_sent, team.totals.quotes_sent),
        quoteShareText: shareText(p.quotes_sent, team.totals.quotes_sent),
        linesPriced: p.lines_priced,
        quotedValue: Number(p.quoted_value),
        speed: p.speed,
        fewQuotes: p.speed.answered > 0 && p.speed.answered < FEW_QUOTES,
        atSystemPct: share(p.lines_at_system_price, p.lines_with_suggestion),
        atSystem: p.lines_at_system_price,
        withSuggestion: p.lines_with_suggestion,
        drill: personDrill(p, from, to),
        speedDrill: personSpeedDrill(p, from, to),
        atSystemDrill: personPricingDrill(p, team),
      };
    })
    .sort((a, b) => Number(a.notRecorded) - Number(b.notRecorded) || b.quotes - a.quotes || a.name.localeCompare(b.name));
}

/** The Team tab's headline tiles, over the selected period. */
export function teamTiles(team: OwnerTeam): ExecTile[] {
  const { from, to } = team.range;
  const t = team.totals;
  const u = team.unattributed;
  const named = team.people.filter((p) => !isNotRecorded(p.key)).length;
  const allQuotes: DrillSpec = {
    title: 'Quotes sent through the system',
    kind: 'system_quotes',
    from,
    to,
    note: 'Every quote sent through the system in the period, with who sent it. Wait (clock) is the time from the RFQ reaching the system to the quote going out.',
  };
  return [
    {
      id: 'quotes',
      label: 'Quotes sent through the system',
      value: nf(t.quotes_sent),
      detail: `${nf(t.lines_priced)} priced lines · ${named} ${named === 1 ? 'person' : 'people'} sent them`,
      drill: allQuotes,
    },
    {
      id: 'value',
      label: 'Quoted value',
      value: formatSgdCompact(t.quoted_value),
      detail: 'Price sent x quantity on those quotes. What was quoted, not what was ordered - orders are on Business overview.',
      drill: allQuotes,
    },
    {
      id: 'speed',
      label: 'Median time to quote',
      value: formatClock(t.speed.median_minutes),
      detail: `From the RFQ reaching the system to the quote going out. ${formatPct(t.speed.pct_within_1h)} within 1 h, ${formatPct(t.speed.pct_within_4h)} within 4 h · ${nf(t.speed.answered)} RFQs that arrived Mon-Fri 09:00-18:00`,
      drill: teamSpeedDrill(team),
    },
    {
      id: 'system-price',
      label: 'Lines sent at the system’s price',
      value: formatPct(share(t.lines_at_system_price, t.lines_with_suggestion)),
      detail: `${nf(t.lines_at_system_price)} of ${nf(t.lines_with_suggestion)} lines that had a system suggestion`,
      drill: {
        title: 'Priced lines on quotes sent',
        kind: 'pricing_lines',
        from,
        to,
        note: 'Each line sent with a system suggestion. Within tolerance = the price sent is within max(2%, a minimum by price band) of the suggestion.',
      },
    },
    {
      id: 'by-hand',
      label: 'Replies sent by hand',
      value: nf(u.manual_replies),
      detail: `of ${nf(u.threads)} RFQs received. Sent from Outlook, so they cannot be attributed to a person.`,
      drill: unattributedDrill(team),
    },
  ];
}

/** Everyone's quotes behind the team median time to quote. */
export function teamSpeedDrill(team: OwnerTeam): DrillSpec {
  const { from, to } = team.range;
  const t = team.totals;
  const all: DrillSpec = { title: 'Quotes sent through the system', kind: 'system_quotes', from, to };
  return {
    title: 'Quotes for office-hours RFQs',
    kind: 'system_quotes',
    from,
    to,
    filter: { office: true },
    note: 'The quotes behind the median time to quote: RFQs that reached the system Mon-Fri 09:00-18:00. Wait (clock) runs from the RFQ reaching the system to the quote going out.',
    unlessIgnored: { ignoredTotal: t.quotes_sent, expected: t.speed.answered, fallback: all },
  };
}

export function unattributedDrill(team: OwnerTeam): DrillSpec {
  return {
    title: 'RFQs answered by hand from Outlook',
    kind: 'rfqs',
    from: team.range.from,
    to: team.range.to,
    filter: { channel: 'manual' },
    note: 'RFQ threads in the sales mailbox, received in the period, whose first reply was sent by hand rather than through the system. The mailbox does not record who sent them.',
  };
}

/** The plain sentence under the table about replies no one can be named for. */
export function unattributedSentence(team: OwnerTeam): string {
  const u = team.unattributed;
  if (u.threads === 0) return 'No RFQs reached the sales mailbox in this period.';
  const speed = u.manual_speed.answered
    ? ` Their median first reply was ${formatClock(u.manual_speed.median_minutes)} (${nf(u.manual_speed.answered)} that arrived in office hours).`
    : '';
  return `${nf(u.manual_replies)} ${u.manual_replies === 1 ? 'reply was' : 'replies were'} sent by hand from Outlook and cannot be attributed to a person, against ${nf(u.system_replies)} sent through the system, of ${nf(u.threads)} RFQs received.${speed}`;
}

/**
 * Quoted value per month for the Business overview panel. Months before the
 * first system quote in the period are dropped (the system was not live yet,
 * and a run of zero bars would read as no business).
 */
export function quotedValueMonths(team: OwnerTeam, today: string): Array<TeamMonth & { label: string; current: boolean; drill: DrillSpec }> {
  const first = team.monthly.findIndex((m) => m.system_quotes > 0);
  if (first < 0) return [];
  const thisMonth = `${today.slice(0, 7)}-01`;
  return team.monthly.slice(first).map((m) => ({
    ...m,
    quoted_value: Number(m.quoted_value),
    label: monthLabel(m.month),
    current: m.month === thisMonth,
    drill: {
      title: `Quotes sent through the system, ${monthLabel(m.month)}`,
      kind: 'system_quotes',
      from: m.month < team.range.from ? team.range.from : m.month,
      to: monthEnd(m.month) > team.range.to ? team.range.to : monthEnd(m.month),
      note: 'Quoted value is price sent x quantity on these quotes - what was quoted, not what was ordered.',
    },
  }));
}
